import type { CustomerSuggestion, CustomerUpdateInput, CustomerView } from "../api";

/**
 * Kunde card rows (#588) — the pure part of the redesigned editing surface.
 *
 * The card used to be an all-or-nothing form over four blank inputs. It now
 * renders one row per field carrying three things the operator needs before
 * touching anything: the stored value, where that value came from, and what
 * the request offers instead. Deciding all three is pure, so it is tested
 * without a browser.
 */

export type CustomerEditableField = "name" | "company" | "phone" | "notes";

export interface CustomerFieldEditorPresence {
  /** Field that currently owns editing actions. */
  active: CustomerEditableField | null;
  /** Editor kept in the DOM long enough to finish an interruptible exit. */
  presented: CustomerEditableField | null;
}

export type CustomerFieldEditorPresenceAction =
  | { type: "begin"; field: CustomerEditableField }
  | { type: "leave"; field: CustomerEditableField }
  | { type: "finish-leave"; field: CustomerEditableField };

export const EMPTY_CUSTOMER_FIELD_EDITOR_PRESENCE: CustomerFieldEditorPresence = {
  active: null,
  presented: null,
};

/**
 * Keep one editor present through its exit, and allow only that same field to
 * reverse the transition. This prevents a rapid cancel/reopen from remounting
 * or losing the draft while still refusing a second editor in another row.
 */
export function customerFieldEditorPresence(
  state: CustomerFieldEditorPresence,
  action: CustomerFieldEditorPresenceAction,
): CustomerFieldEditorPresence {
  if (action.type === "begin") {
    if (state.presented !== null && state.presented !== action.field) return state;
    return { active: action.field, presented: action.field };
  }
  if (action.type === "leave") {
    return state.active === action.field ? { ...state, active: null } : state;
  }
  if (state.active === null && state.presented === action.field) {
    return EMPTY_CUSTOMER_FIELD_EDITOR_PRESENCE;
  }
  return state;
}

/** Rendered in this order; `notes` is operator-only and never has a suggestion. */
export const CUSTOMER_CARD_FIELDS: readonly CustomerEditableField[] = [
  "name",
  "company",
  "phone",
  "notes",
];

/** i18n key for the provenance line under a filled field. */
export type CustomerProvenanceKey =
  | "ws.customer.fromMail"
  | "ws.customer.fromOperator"
  | "ws.customer.fromUnknown";

/** i18n key introducing an offered value — it must not misattribute it either. */
export type CustomerSuggestionTitleKey =
  | "ws.customer.suggestionTitle"
  | "ws.customer.suggestionTitleOperator";

export function customerSuggestionTitleKey(
  suggestion: CustomerSuggestion,
): CustomerSuggestionTitleKey {
  return suggestion.source === "operator"
    ? "ws.customer.suggestionTitleOperator"
    : "ws.customer.suggestionTitle";
}

/** The value the last operator change replaced, offered back (#606). */
export interface CustomerFieldRestore {
  /** What to put back; null restores the field to empty. */
  value: string | null;
}

export interface CustomerFieldRow {
  field: CustomerEditableField;
  /** Stored value, or null when the field is empty. */
  value: string | null;
  /** Null only for an empty field — there is nothing to attribute yet. */
  provenanceKey: CustomerProvenanceKey | null;
  /** A value the request offers instead, unless the operator waved it away. */
  suggestion: CustomerSuggestion | null;
  /** Undo for the last overwrite, available whether or not a fact backs it. */
  restore: CustomerFieldRestore | null;
  /**
   * The offered value and the undo target are the same, so the card *is* the
   * undo: it keeps the mail's evidence and attribution, and carries the restore
   * action instead of a second control saying the same thing.
   */
  restoreIsSuggestion: boolean;
}

/**
 * Identity of a suggestion for local dismissal. Keyed by value as well as
 * field, so waving away one reading does not also hide a *different* value a
 * later mail proposes for the same field.
 */
export function customerSuggestionKey(suggestion: CustomerSuggestion): string {
  return `${suggestion.field}::${suggestion.value}`;
}

export function customerFieldRows(
  customer: CustomerView,
  suggestions: readonly CustomerSuggestion[],
  dismissed: ReadonlySet<string>,
): CustomerFieldRow[] {
  return CUSTOMER_CARD_FIELDS.map((field) => {
    const value = customer[field];
    const source = customer.field_sources?.[field];
    const offered = suggestions.find(
      (suggestion) => suggestion.field === field && !dismissed.has(customerSuggestionKey(suggestion)),
    );
    // Nothing to undo once the field is back to what it held before.
    const stored = customer.field_previous?.[field];
    const restore = stored !== undefined && stored !== value ? { value: stored } : null;
    // Two controls for one outcome is noise — but the card is the only place
    // that shows *why* the mail says this, so the card wins and takes over the
    // restore action rather than being replaced by a bare link.
    const restoreIsSuggestion = restore !== null && offered !== undefined
      && offered.value === restore.value;
    return {
      field,
      value,
      provenanceKey: value === null || value === "" ? null : provenanceKey(source),
      suggestion: offered ?? null,
      restore,
      restoreIsSuggestion,
    };
  });
}

function provenanceKey(source: string | undefined): CustomerProvenanceKey {
  if (source === "extraction") return "ws.customer.fromMail";
  if (source === "operator") return "ws.customer.fromOperator";
  // A stored value the server could not attribute. Saying so is better than
  // guessing an origin the operator would then trust.
  return "ws.customer.fromUnknown";
}

/**
 * Patch for a single field. Per-field saves are what keep an untouched field
 * safe from a concurrent edit: only the one field travels, under the version
 * the operator actually saw.
 */
export function customerFieldPatch(
  field: CustomerEditableField,
  raw: string,
  expectedVersion: number,
): CustomerUpdateInput {
  const value = raw.trim();
  return { [field]: value === "" ? null : value, expected_version: expectedVersion };
}

/**
 * DOM contract between the card and the focus restoration in ClientPanel.
 * Built in one place because losing focus to the document is invisible to the
 * type checker: a rename on one side would only surface as a failing e2e.
 */
export function customerFieldInputId(field: CustomerEditableField): string {
  return `cust-${field}`;
}

/** Attribute marking a row's edit control as a focus target (not a test hook). */
export function customerFieldEditAttr(field: CustomerEditableField): Record<string, string> {
  return { "data-cust-edit": field };
}

export function customerFieldEditSelector(field: CustomerEditableField): string {
  return `[data-cust-edit="${field}"]`;
}
